import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLayers, FiCpu, FiTerminal, FiCloud, FiEye, FiSearch, FiArrowRight, FiCheckCircle } = FiIcons;

const services = [
  {
    icon: FiLayers,
    id: "software",
    title: "Custom Enterprise Software",
    desc: "We build robust, scalable software tailored to your exact business processes — from ERP and CRM systems to bespoke internal tools that fit how your team actually works.",
    deliverables: ["Full-stack web applications", "Internal business tools & dashboards", "Database design & migration", "Third-party system integrations"],
    stack: [".NET Core", "React", "SQL Server", "Azure"],
    useCase: "A manufacturing client replaced 3 disconnected spreadsheets with a unified ERP — cutting manual data entry by 70%."
  },
  {
    icon: FiCpu,
    id: "ai",
    title: "AI Integration & Intelligent Automation",
    desc: "We embed AI into your existing or new systems — automating workflows, reducing manual effort, and enabling data-driven decisions through machine learning.",
    deliverables: ["ML model development & training", "AI-powered workflow automation", "Data pipeline engineering", "Model monitoring & retraining"],
    stack: ["Python", "TensorFlow", "PyTorch", "Azure ML"],
    useCase: "A logistics firm automated shipment document processing with computer vision — reducing processing time by 85%.",
    link: "/ai-integration"
  },
  {
    icon: FiTerminal,
    id: "dotnet",
    title: "Web API & .NET Development",
    desc: "Secure, high-performance backend systems and APIs built on .NET, MVC, and Web API — engineered to integrate seamlessly with your infrastructure.",
    deliverables: ["RESTful API design & development", "Microservices architecture", "Authentication & authorization layers", "Performance optimization & caching"],
    stack: [".NET 8", "Web API", "Entity Framework", "Redis"],
    useCase: "Built a multi-tenant API gateway handling 10,000+ requests/minute for a retail platform with zero downtime."
  },
  {
    icon: FiCloud,
    id: "cloud",
    title: "Cloud Architecture (Microsoft Azure)",
    desc: "End-to-end cloud deployment, management, and scaling on Microsoft Azure — including infrastructure design, DevOps pipelines, and monitoring.",
    deliverables: ["Cloud migration & architecture design", "CI/CD pipeline setup", "Infrastructure as Code (IaC)", "Cost optimization & monitoring"],
    stack: ["Azure App Services", "Azure DevOps", "Docker", "Terraform"],
    useCase: "Migrated an on-premise ERP to Azure — reducing hosting costs by 40% while improving uptime to 99.9%."
  },
  {
    icon: FiEye,
    id: "vision",
    title: "Deep Learning & Computer Vision",
    desc: "Custom AI models for image recognition, object detection, and visual data analysis — built on TensorFlow and PyTorch for production-grade performance.",
    deliverables: ["Custom model training & fine-tuning", "Image classification & object detection", "OCR & document extraction", "Edge deployment for real-time inference"],
    stack: ["TensorFlow", "PyTorch", "OpenCV", "ONNX Runtime"],
    useCase: "Deployed a defect detection model on a production line that catches quality issues with 97% accuracy in real-time.",
    link: "/ai-integration"
  },
  {
    icon: FiSearch,
    id: "consulting",
    title: "Technology Consulting",
    desc: "Strategic technology advisory to audit your current systems, identify inefficiencies, and design the right architecture — before a single line of code is written.",
    deliverables: ["Technical architecture review", "System audit & gap analysis", "Technology roadmap planning", "Build vs. buy recommendations"],
    stack: ["Architecture Diagrams", "Risk Assessment", "Cost Modelling"],
    useCase: "Helped a healthcare firm avoid a 6-figure platform rebuild by identifying targeted improvements to their existing .NET stack."
  }
];

const Services = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-32 pb-24 relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.3]"></div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20" >
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6">Our Services</h1>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg leading-relaxed">
            Every engagement at Aetrix Logic is built around one goal — delivering technology that creates measurable impact for your business.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group p-8 bg-white border border-slate-200 hover:border-cyan-300 transition-all duration-300 relative overflow-hidden shadow-sm hover:shadow-md"
            >
              <div className="absolute top-0 left-0 w-1 h-0 bg-cyan-500 group-hover:h-full transition-all duration-500"></div>
              <div className="flex items-start gap-6 mb-6">
                <div className="w-14 h-14 bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={service.icon} className="text-2xl text-cyan-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
                </div>
              </div>

              {/* Deliverables */}
              <div className="mb-6">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Typical Deliverables</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {service.deliverables.map((item, dIndex) => (
                    <li key={dIndex} className="flex items-start gap-2 text-sm text-slate-700">
                      <SafeIcon icon={FiCheckCircle} className="text-cyan-500 text-xs mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tech Stack */}
              <div className="mb-6">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Tech Stack</h4>
                <div className="flex flex-wrap gap-2">
                  {service.stack.map((tech, tIndex) => (
                    <span key={tIndex} className="px-3 py-1 bg-slate-50 border border-slate-200 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Use Case */}
              <div className="p-4 bg-cyan-50/50 border border-cyan-100 mb-6">
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-cyan-600 mb-2">Example Outcome</h4>
                <p className="text-xs text-slate-600 leading-relaxed italic">{service.useCase}</p>
              </div>

              <button
                onClick={() => service.link ? navigate(service.link) : navigate('/contact')}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cyan-600 hover:text-slate-900 transition-colors group/link"
              >
                {service.link ? 'Explore AI Capabilities' : 'Inquire Now'}
                <SafeIcon icon={FiArrowRight} className="group-hover/link:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Cross-link to Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 p-8 bg-slate-50 border border-slate-100 text-center"
        >
          <p className="text-slate-600 mb-4">
            See how we've applied these services across manufacturing, logistics, retail, healthcare, and finance.
          </p>
          <button
            onClick={() => navigate('/capabilities')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white font-bold uppercase tracking-widest text-xs hover:bg-black transition-all group"
          >
            View Industry Capabilities
            <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;